import React from 'react';
import { Link } from 'react-router-dom';

// Curated internal links into the generated landing pages. These give crawlers a
// path from the app to the ~136 static pages, which is how they get discovered.
const FOOTER_CITIES = [
  { slug: 'vancouver', name: 'Vancouver' },
  { slug: 'surrey', name: 'Surrey' },
  { slug: 'burnaby', name: 'Burnaby' },
  { slug: 'richmond', name: 'Richmond' },
  { slug: 'langley', name: 'Langley' },
  { slug: 'kelowna', name: 'Kelowna' },
  { slug: 'victoria', name: 'Victoria' },
];

const FOOTER_REGIONS = [
  { slug: 'metro-vancouver', name: 'Metro Vancouver' },
  { slug: 'vancouver-island-and-the-gulf-islands', name: 'Vancouver Island' },
  { slug: 'okanagan', name: 'Okanagan' },
];

const Footer = () => {
  return (
    <footer className="tiu-footer text-white">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and About Section Side by Side */}
          <div className="flex flex-col md:flex-row items-start">
            <Link to="/" className="flex-shrink-0">
              <img src="/assets/Logo1.png" alt="Tee It Up Logo" className="h-24 w-auto md:mr-6 mr-0 mb-4 md:mb-0" />
            </Link>
            <div className="text-left">
              <h3 className="text-lg font-semibold mb-2 mt-2 md:mt-0">About Tee It Up</h3>
              <p className=" text-sm">
                Helping golfers compare live tee times across Vancouver and British Columbia's public golf courses.
              </p>
            </div>
          </div>

          {/* Popular cities. Plain <a> on purpose: these are statically generated
              pages served ahead of the SPA rewrite, not react-router routes. */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-4">Tee times by city</h3>
            <ul className="space-y-2">
              {FOOTER_CITIES.map(({ slug, name }) => (
                <li key={slug}>
                  <a href={`/tee-times/${slug}`} className="hover:text-white text-sm">
                    {name} tee times
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Browse */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-4">Browse</h3>
            <ul className="space-y-2">
              <li><a href="/golf-courses" className="hover:text-white text-sm">All BC golf courses</a></li>
              {FOOTER_REGIONS.map(({ slug, name }) => (
                <li key={slug}>
                  <a href={`/golf-courses/${slug}`} className="hover:text-white text-sm">{name}</a>
                </li>
              ))}
              <li><a href="/" className="hover:text-white text-sm">Search tee times</a></li>
            </ul>
          </div>

          {/* Social Media Links */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              {/* Facebook */}
              <a 
                href="https://www.facebook.com/profile.php?id=61575702465958" 
                target="_blank" 
                rel="noopener noreferrer"
                className=" hover:text-blue-400 transition-colors duration-200"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>

              {/* Instagram */}
              <a 
                href="https://www.instagram.com/teeitup_yvr/" 
                target="_blank" 
                rel="noopener noreferrer"
                className=" hover:text-pink-400 transition-colors duration-200"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 01-2.88 0 1.44 1.44 0 012.88 0z"/>
                </svg>
              </a>

              {/* TikTok */}
              <a 
                href="https://www.tiktok.com/@teeitup_yvr?lang=en" 
                target="_blank" 
                rel="noopener noreferrer"
                className=" hover:text-black transition-colors duration-200"
                aria-label="TikTok"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.10)' }}>
          <p className="tiu-footer-note text-sm text-center">
            © {new Date().getFullYear()} Tee It Up. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 